import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { writeStoredListCreatedBy } from '../lib/createdByScopeStorage';
import { isOverviewPrivileged, isOverviewProcessor } from '../lib/portalRoles';
import { FALLBACK_STATUS_LABELS } from '../lib/referralStatus';
import { ensureIncidentLookup, } from '../services/incidentLookup';
import { fetchOverviewUsers, } from '../services/referrals';
import { ensureSignInUserInfo } from '../services/signinUserInfo';
import { DEFAULT_REFERRAL_FILTERS } from '../types/referral';
import { ALL_INCIDENT_LEVELS } from '../lib/incidentLevels';
function statusFilterOptions(statuses) {
    const fromLookup = statuses.map((entry) => ({
        value: entry.value,
        label: entry.name,
    }));
    const options = fromLookup.length > 0
        ? fromLookup
        : Object.entries(FALLBACK_STATUS_LABELS).map(([value, label]) => ({
            value: Number(value),
            label,
        }));
    return [{ value: null, label: 'All statuses' }, ...options];
}
function siteFilterOptions(sites) {
    const sorted = [...sites].sort((a, b) => (a.name ?? `Site ${a.id}`).localeCompare(b.name ?? `Site ${b.id}`, undefined, {
        sensitivity: 'base',
    }));
    return [
        { value: null, label: 'All sites' },
        ...sorted.map((site) => ({
            value: site.id,
            label: site.name?.trim() || `Site ${site.id}`,
        })),
    ];
}
function issueTypeFilterOptions(issueTypes) {
    const visible = issueTypes.filter((entry) => {
        if (entry.visible === false)
            return false;
        const name = (entry.name ?? '').trim();
        if (/L1\s*\.?\s*5/i.test(name) || /^L2\b/i.test(name))
            return false;
        return true;
    });
    const roots = visible
        .filter((entry) => entry.parentId == null || entry.parentId === 0)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));
    const children = visible
        .filter((entry) => entry.parentId != null && entry.parentId !== 0)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));
    const options = [];
    for (const root of roots) {
        options.push({
            value: root.id,
            label: root.name?.trim() || `Type ${root.id}`,
            isChild: false,
        });
        for (const child of children.filter((entry) => entry.parentId === root.id)) {
            options.push({
                value: child.id,
                label: child.name?.trim() || `Type ${child.id}`,
                isChild: true,
            });
        }
    }
    return options;
}
function userDisplayName(user) {
    const name = `${user.firstName} ${user.lastName}`.trim();
    return name || user.email || `User ${user.id}`;
}
function sortUsersByDisplayName(users) {
    return [...users].sort((a, b) => {
        const cmp = userDisplayName(a).localeCompare(userDisplayName(b), undefined, {
            sensitivity: 'base',
        });
        if (cmp !== 0)
            return cmp;
        return a.id - b.id;
    });
}
function roleNameFromDetail(detail) {
    return (detail?.roleName?.trim() ||
        (typeof detail?.role?.name === 'string' ? detail.role.name.trim() : '') ||
        '');
}
export function ReferralFiltersBar({ filters, onChange }) {
    const [expanded, setExpanded] = useState(true);
    const [draft, setDraft] = useState(filters);
    const [statuses, setStatuses] = useState([]);
    const [sites, setSites] = useState([]);
    const [issueTypes, setIssueTypes] = useState([]);
    const [issueTypeMenuOpen, setIssueTypeMenuOpen] = useState(false);
    const issueTypeMenuRef = useRef(null);
    const [viewerId, setViewerId] = useState(null);
    const [viewerRoleName, setViewerRoleName] = useState('');
    const [roleReady, setRoleReady] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const privileged = isOverviewPrivileged(viewerRoleName);
    const processor = isOverviewProcessor(viewerRoleName);
    const showCreatedBy = privileged || processor;
    const sortedUsers = useMemo(() => sortUsersByDisplayName(users), [users]);
    useEffect(() => {
        setDraft(filters);
    }, [filters]);
    useEffect(() => {
        ensureIncidentLookup()
            .then((lookup) => {
            setStatuses(lookup.statuses);
            setSites(lookup.sites);
            setIssueTypes(lookup.issueTypes);
        })
            .catch((err) => console.error('[incidentLookup] lookup failed', err));
    }, []);
    useEffect(() => {
        if (!issueTypeMenuOpen)
            return;
        const onPointerDown = (event) => {
            if (!issueTypeMenuRef.current?.contains(event.target)) {
                setIssueTypeMenuOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                setIssueTypeMenuOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [issueTypeMenuOpen]);
    useEffect(() => {
        let cancelled = false;
        ensureSignInUserInfo()
            .then((info) => {
            if (cancelled)
                return;
            const detail = info.detail;
            const id = typeof detail?.id === 'number' ? detail.id : null;
            setViewerId(id);
            setViewerRoleName(roleNameFromDetail(detail));
            setRoleReady(true);
        })
            .catch((err) => {
            console.error('[referral-filters] signin user failed', err);
            if (!cancelled)
                setRoleReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        if (!roleReady || !privileged)
            return;
        let cancelled = false;
        setUsersLoading(true);
        fetchOverviewUsers()
            .then((list) => {
            if (!cancelled)
                setUsers(list);
        })
            .catch((err) => {
            console.error('[referral-filters] overview users failed', err);
            if (!cancelled)
                setUsers([]);
        })
            .finally(() => {
            if (!cancelled)
                setUsersLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [roleReady, privileged]);
    const handleSubmit = (event) => {
        event.preventDefault();
        setIssueTypeMenuOpen(false);
        onChange(draft);
    };
    const persistCreatedBy = (createdBy) => {
        if (viewerId == null || (!privileged && !processor))
            return;
        writeStoredListCreatedBy(viewerId, processor ? 'processor' : 'privileged', createdBy);
    };
    const handleReset = () => {
        const reset = {
            ...DEFAULT_REFERRAL_FILTERS,
            // Processors default back to "My L1s".
            createdBy: processor && viewerId != null ? viewerId : null,
        };
        persistCreatedBy(reset.createdBy);
        setDraft(reset);
        setIssueTypeMenuOpen(false);
        onChange(reset);
    };
    const toggleIssueTypeId = (id) => {
        setDraft((prev) => {
            const selected = new Set(prev.issueTypeIds);
            if (selected.has(id))
                selected.delete(id);
            else
                selected.add(id);
            return { ...prev, issueTypeIds: [...selected].sort((a, b) => a - b) };
        });
    };
    const activeFilterCount = [
        filters.search.trim() !== '',
        filters.status !== null,
        filters.dateFrom !== '',
        filters.dateTo !== '',
        filters.siteId !== null,
        filters.issueTypeIds.length > 0,
        filters.levels.length > 0 && filters.levels.length < ALL_INCIDENT_LEVELS.length,
        showCreatedBy &&
            (processor
                ? viewerId != null && filters.createdBy !== viewerId
                : filters.createdBy != null),
    ].filter(Boolean).length;
    const statusOptions = statusFilterOptions(statuses);
    const siteOptions = siteFilterOptions(sites);
    const issueTypeOptions = issueTypeFilterOptions(issueTypes);
    const selectedIssueTypeCount = draft.issueTypeIds.length;
    const issueTypeSummary = selectedIssueTypeCount === 0
        ? 'All incident types'
        : selectedIssueTypeCount === 1
            ? issueTypeOptions.find((option) => option.value === draft.issueTypeIds[0])?.label ||
                '1 type selected'
            : `${selectedIssueTypeCount} types selected`;
    const createdBySelectValue = (() => {
        if (processor) {
            return draft.createdBy != null && viewerId != null && draft.createdBy === viewerId
                ? 'mine'
                : 'org';
        }
        return draft.createdBy != null ? String(draft.createdBy) : 'org';
    })();
    const onCreatedByChange = (value) => {
        if (processor) {
            const createdBy = value === 'mine' && viewerId != null ? viewerId : null;
            persistCreatedBy(createdBy);
            setDraft({ ...draft, createdBy });
            return;
        }
        if (value === '' || value === 'org') {
            persistCreatedBy(null);
            setDraft({ ...draft, createdBy: null });
            return;
        }
        const id = Number(value);
        if (!Number.isFinite(id) || id <= 0)
            return;
        persistCreatedBy(id);
        setDraft({ ...draft, createdBy: id });
    };
    return (_jsxs("section", { className: `filters-panel${expanded ? ' is-expanded' : ''}`, children: [_jsxs("button", { type: "button", className: "filters-toggle", "aria-expanded": expanded, "aria-controls": "referral-filters", onClick: () => setExpanded((current) => !current), children: [_jsxs("span", { className: "filters-toggle-label", children: [_jsx("svg", { className: "filters-icon", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M4 5h16l-6.2 7.1v5.4l-3.6 1.8v-7.2L4 5Z" }) }), "Filter"] }), _jsxs("span", { className: "filters-toggle-meta", children: [activeFilterCount > 0 && (_jsxs("span", { className: "filters-active-count", children: [activeFilterCount, " active"] })), _jsx("svg", { className: "filters-chevron", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "m7 9 5 5 5-5" }) })] })] }), expanded && (_jsxs("form", { id: "referral-filters", className: "filters-bar", onSubmit: handleSubmit, children: [_jsxs("label", { className: "filter-field filter-field-grow", children: ["Search L1s", _jsx("input", { type: "search", placeholder: "Search text, L1 ID, or Case ID", value: draft.search, onChange: (e) => setDraft({ ...draft, search: e.target.value }) })] }), _jsxs("label", { className: "filter-field", children: ["L1 status", _jsx("select", { value: draft.status ?? '', onChange: (e) => {
                                    const raw = e.target.value;
                                    setDraft({
                                        ...draft,
                                        status: raw === '' ? null : Number(raw),
                                    });
                                }, children: statusOptions.map((option) => (_jsx("option", { value: option.value ?? '', children: option.label }, `${option.value ?? 'all'}-${option.label}`))) })] }), _jsxs("div", { className: "filter-field filter-field-grow filter-field-multiselect", ref: issueTypeMenuRef, children: [_jsx("span", { className: "filter-field-label", children: "Incident type" }), _jsxs("button", { type: "button", className: `filter-multiselect-trigger${issueTypeMenuOpen ? ' is-open' : ''}`, "aria-expanded": issueTypeMenuOpen, "aria-haspopup": "listbox", onClick: () => setIssueTypeMenuOpen((open) => !open), children: [_jsx("span", { className: "filter-multiselect-summary", children: issueTypeSummary }), _jsx("span", { className: "filter-multiselect-chevron", "aria-hidden": "true", children: "\u25BE" })] }), issueTypeMenuOpen && (_jsxs("div", { className: "filter-multiselect-panel", role: "listbox", "aria-multiselectable": "true", children: [_jsx("div", { className: "filter-multiselect-actions", children: _jsx("button", { type: "button", className: "btn btn-ghost filter-multiselect-action", onClick: () => setDraft({ ...draft, issueTypeIds: [] }), children: "Clear" }) }), _jsx("ul", { className: "filter-multiselect-options", children: issueTypeOptions.map((option) => {
                                            const checked = draft.issueTypeIds.includes(option.value);
                                            return (_jsx("li", { children: _jsxs("label", { className: `filter-multiselect-option${option.isChild ? ' is-child' : ''}`, children: [_jsx("input", { type: "checkbox", checked: checked, onChange: () => toggleIssueTypeId(option.value) }), _jsx("span", { children: option.label })] }) }, option.value));
                                        }) })] }))] }), _jsxs("fieldset", { className: "filter-field filter-level-chips", children: [_jsx("legend", { className: "filter-field-label", children: "Level" }), _jsx("div", { className: "level-chip-list", role: "group", "aria-label": "Incident level", children: ALL_INCIDENT_LEVELS.map((level) => {
                                    const checked = draft.levels.includes(level);
                                    return (_jsxs("label", { className: `level-chip${checked ? ' is-selected' : ''}`, children: [_jsx("input", { type: "checkbox", checked: checked, onChange: () => {
                                                    setDraft((prev) => {
                                                        const selected = new Set(prev.levels);
                                                        if (selected.has(level))
                                                            selected.delete(level);
                                                        else
                                                            selected.add(level);
                                                        const next = ALL_INCIDENT_LEVELS.filter((item) => selected.has(item));
                                                        return {
                                                            ...prev,
                                                            levels: next.length > 0 ? next : [...ALL_INCIDENT_LEVELS],
                                                        };
                                                    });
                                                } }), level] }, level));
                                }) })] }), _jsxs("label", { className: "filter-field", children: ["Site", _jsx("select", { value: draft.siteId ?? '', onChange: (e) => {
                                    const raw = e.target.value;
                                    setDraft({
                                        ...draft,
                                        siteId: raw === '' ? null : Number(raw),
                                    });
                                }, children: siteOptions.map((option) => (_jsx("option", { value: option.value ?? '', children: option.label }, `${option.value ?? 'all'}-${option.label}`))) })] }), showCreatedBy && (_jsxs("label", { className: "filter-field", children: ["Created by", _jsx("select", { value: createdBySelectValue, onChange: (e) => onCreatedByChange(e.target.value), disabled: privileged && usersLoading, children: processor ? (_jsxs(_Fragment, { children: [_jsx("option", { value: "mine", children: "My L1s" }), _jsx("option", { value: "org", children: "Entire organization" })] })) : (_jsxs(_Fragment, { children: [_jsx("option", { value: "org", children: "Entire organization" }), sortedUsers.map((user) => (_jsxs("option", { value: user.id, children: [userDisplayName(user), user.roleName ? ` (${user.roleName})` : ''] }, user.id)))] })) })] })), _jsxs("label", { className: "filter-field", children: ["Created after", _jsx("input", { type: "date", value: draft.dateFrom, onChange: (e) => setDraft({ ...draft, dateFrom: e.target.value }) })] }), _jsxs("label", { className: "filter-field", children: ["Created before", _jsx("input", { type: "date", value: draft.dateTo, onChange: (e) => setDraft({ ...draft, dateTo: e.target.value }) })] }), _jsxs("div", { className: "filter-actions", children: [_jsx("button", { type: "button", className: "btn btn-secondary", onClick: handleReset, children: "Reset" }), _jsx("button", { type: "submit", className: "btn btn-primary", children: "Search" })] })] }))] }));
}
